import React, { useCallback, useEffect, useState } from "react";
import { Download, Check, X, Search, Eye, Package, Coins } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const fmtDate = (iso) => { if (!iso) return "—"; const [y, m, d] = String(iso).split("-"); return y && m && d ? `${d}-${m}-${y}` : iso; };
const STATUS_CLR = {
  Submitted: "bg-sky-100 text-sky-700",
  "Under Verification": "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-700",
  Paid: "bg-violet-100 text-violet-700",
};

export function AdminInvoices({ token, onUnauthorized }) {
  const [invoices, setInvoices] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rejecting, setRejecting] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [viewing, setViewing] = useState(null);
  const [viewingPO, setViewingPO] = useState(null);
  const auth = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/admin/invoices`, { headers: auth });
    if (r.status === 401) return onUnauthorized();
    setInvoices(await r.json());
  }, [token, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (inv, status, extraRemarks = "") => {
    const r = await fetch(`${API_BASE}/api/admin/invoices/${inv.id}/status`, {
      method: "PUT", headers: { ...auth, "Content-Type": "application/json" },
      body: JSON.stringify({ status, remarks: extraRemarks }),
    });
    if (!r.ok) return alert((await r.json()).detail || "Failed");
    setRejecting(null); setRemarks("");
    await load();
  };

  const downloadPdf = async (inv) => {
    const r = await fetch(`${API_BASE}/api/admin/invoices/${inv.id}/pdf`, { headers: auth });
    if (!r.ok) return alert("Invoice PDF not found");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = inv.pdf_filename || `${inv.invoice_number}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const openDetails = async (inv) => {
    setViewing(inv);
    setViewingPO(null);
    if (inv.po_id) {
      const r = await fetch(`${API_BASE}/api/admin/purchase-orders/${inv.po_id}`, { headers: auth });
      if (r.ok) setViewingPO(await r.json());
    }
  };

  const filtered = (invoices || []).filter((i) => {
    if (statusFilter && i.status !== statusFilter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return (i.invoice_number + " " + (i.vendor_name || "") + " " + (i.po_number || "")).toLowerCase().includes(s);
  });

  return (
    <div data-testid="admin-invoices">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice/vendor/PO…" className="pl-9" data-testid="invoice-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="invoice-status-filter" className="h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
          <option value="">All statuses</option>
          {Object.keys(STATUS_CLR).map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">ID</th>
              <th className="px-4 py-3 font-semibold">Invoice No</th>
              <th className="px-4 py-3 font-semibold">Vendor</th>
              <th className="px-4 py-3 font-semibold">PO Number</th>
              <th className="px-4 py-3 font-semibold">Date</th>
              <th className="px-4 py-3 font-semibold text-right">Amount</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!invoices && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {invoices && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No invoices.</td></tr>}
            {filtered.map((inv) => (
              <tr key={inv.id} className="border-t border-slate-100 hover:bg-sky-50/50">
                <td className="px-4 py-3 font-mono text-xs font-bold text-slate-500">#{inv.invoice_ref || "—"}</td>
                <td className="px-4 py-3 font-semibold text-[#0a2a5e]">{inv.invoice_number}</td>
                <td className="px-4 py-3">{inv.vendor_name}</td>
                <td className="px-4 py-3 font-mono text-xs">{inv.po_number}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(inv.invoice_date)}</td>
                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">
                  ₹ {inr(inv.invoice_amount)}
                  {inv.misc_charges?.length > 0 && (
                    <div data-testid={`inv-misc-badge-${inv.id}`} className="text-[10px] text-amber-700 font-bold mt-0.5 flex items-center justify-end gap-0.5">
                      <Coins className="h-2.5 w-2.5" /> +{inv.misc_charges.length} misc
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_CLR[inv.status] || ""}`}>{inv.status}</span>
                  {inv.admin_remarks && <div className="text-xs text-slate-500 mt-1 italic">{inv.admin_remarks}</div>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openDetails(inv)} data-testid={`inv-view-${inv.id}`} className="text-sky-600 hover:text-sky-800 p-1.5" title="View invoice details"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => downloadPdf(inv)} data-testid={`inv-pdf-${inv.id}`} className="text-emerald-600 hover:text-emerald-800 p-1.5" title="Download invoice PDF"><Download className="h-4 w-4" /></button>
                  {inv.status === "Submitted" && (
                    <button onClick={() => setStatus(inv, "Under Verification")} data-testid={`inv-verify-${inv.id}`} className="text-amber-600 hover:text-amber-800 px-2 py-1 text-xs font-semibold">Verify</button>
                  )}
                  {["Submitted", "Under Verification"].includes(inv.status) && (
                    <>
                      <button onClick={() => setStatus(inv, "Approved")} data-testid={`inv-approve-${inv.id}`} className="text-emerald-600 hover:text-emerald-800 p-1.5" title="Approve"><Check className="h-4 w-4" /></button>
                      <button onClick={() => setRejecting(inv)} data-testid={`inv-reject-${inv.id}`} className="text-red-500 hover:text-red-700 p-1.5" title="Reject"><X className="h-4 w-4" /></button>
                    </>
                  )}
                  {inv.status === "Approved" && (
                    <button onClick={() => setStatus(inv, "Paid")} data-testid={`inv-mark-paid-${inv.id}`} className="text-violet-600 hover:text-violet-800 px-2 py-1 text-xs font-semibold">Mark Paid</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rejecting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="font-bold text-lg text-[#0a2a5e] mb-2">Reject Invoice {rejecting.invoice_number}</h3>
            <p className="text-sm text-slate-500 mb-4">Provide a reason — this will be visible to the vendor.</p>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="reject-remarks" rows={4} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm mb-4" placeholder="Reason for rejection…" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setRejecting(null); setRemarks(""); }} className="rounded-full">Cancel</Button>
              <Button onClick={() => setStatus(rejecting, "Rejected", remarks)} data-testid="reject-confirm" className="bg-red-600 hover:bg-red-700 text-white rounded-full">Reject Invoice</Button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <InvoiceDetailsModal invoice={viewing} po={viewingPO} onClose={() => { setViewing(null); setViewingPO(null); }} />
      )}
    </div>
  );
}

function InvoiceDetailsModal({ invoice, po, onClose }) {
  const items = invoice.items || [];
  const misc = invoice.misc_charges || [];
  const poItems = po?.items || [];
  const productsBasic = items.reduce((s, it) => {
    const pi = poItems[it.item_index] || {};
    return s + Number(it.invoice_qty || 0) * Number(pi.rate || 0);
  }, 0);
  const productsGst = items.reduce((s, it) => {
    const pi = poItems[it.item_index] || {};
    return s + Number(it.invoice_qty || 0) * Number(pi.rate || 0) * (Number(pi.gst_pct || 0) / 100);
  }, 0);
  const miscBasic = Number(invoice.misc_basic || 0);
  const miscGst = Number(invoice.misc_gst || 0);
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="invoice-details-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg text-[#0a2a5e]">Invoice {invoice.invoice_number}</h3>
            <p className="text-xs text-slate-500">
              <span className="font-mono">{invoice.po_number}</span> · {invoice.vendor_name} · #{invoice.invoice_ref}
              {invoice.has_excess && <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">EXCESS</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> PO Line Items</div>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2 text-right">Ordered</th>
                    <th className="px-3 py-2 text-right">Invoice Qty</th>
                    <th className="px-3 py-2 text-right">Excess</th>
                    <th className="px-3 py-2 text-right">Rate</th>
                    <th className="px-3 py-2 text-right">Basic Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const pi = poItems[it.item_index] || {};
                    const qty = Number(it.invoice_qty || 0);
                    const rate = Number(pi.rate || 0);
                    const excess = Number(it.excess_qty || 0);
                    return (
                      <tr key={i} className={`border-t border-slate-100 ${excess > 0 ? "bg-red-50/50" : ""}`}>
                        <td className="px-3 py-2">
                          <div className="font-semibold">{pi.item || `Item #${it.item_index}`}</div>
                          {pi.description && <div className="text-xs text-slate-500">{pi.description}</div>}
                        </td>
                        <td className="px-3 py-2 text-right">{fmtQty(pi.qty)} {pi.unit}</td>
                        <td className="px-3 py-2 text-right font-semibold">{fmtQty(qty)} {pi.unit}</td>
                        <td className="px-3 py-2 text-right">{excess > 0 ? <span className="text-red-600 font-bold">+{fmtQty(excess)}</span> : "—"}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">₹ {inr(rate)}</td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">₹ {inr(qty * rate)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {misc.length > 0 && (
            <div data-testid="admin-invoice-misc">
              <div className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-2 flex items-center gap-1"><Coins className="h-3.5 w-3.5" /> Miscellaneous / Additional Charges</div>
              <div className="border border-amber-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-amber-50 text-left text-xs uppercase text-amber-800">
                    <tr>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Rate</th>
                      <th className="px-3 py-2 text-right">GST %</th>
                      <th className="px-3 py-2 text-right">Basic</th>
                      <th className="px-3 py-2 text-right">GST</th>
                      <th className="px-3 py-2 text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {misc.map((m, i) => (
                      <tr key={i} className="border-t border-amber-100">
                        <td className="px-3 py-2 font-semibold">{m.description}</td>
                        <td className="px-3 py-2 text-right">{fmtQty(m.qty)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">₹ {inr(m.rate)}</td>
                        <td className="px-3 py-2 text-right">{m.gst_pct}%</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">₹ {inr(m.basic)}</td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">₹ {inr(m.gst_amount)}</td>
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">₹ {inr(m.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-w-sm ml-auto text-sm">
            <div className="flex justify-between py-0.5"><span>Products Basic</span><span>₹ {inr(productsBasic)}</span></div>
            {miscBasic > 0 && <div className="flex justify-between py-0.5 text-amber-800"><span>Misc Basic</span><span>₹ {inr(miscBasic)}</span></div>}
            <div className="flex justify-between py-0.5 font-semibold border-t border-slate-200 mt-1 pt-1"><span>Total Basic (excl. GST)</span><span>₹ {inr(productsBasic + miscBasic)}</span></div>
            <div className="flex justify-between py-0.5"><span>Total GST</span><span>₹ {inr(productsGst + miscGst)}</span></div>
            <div className="flex justify-between border-t-2 border-[#0a2a5e] mt-2 pt-2 font-bold text-[#0a2a5e]"><span>Invoice Total</span><span>₹ {inr(invoice.invoice_amount)}</span></div>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex justify-end bg-slate-50">
          <Button onClick={onClose} className="rounded-full bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white">Close</Button>
        </div>
      </div>
    </div>
  );
}
